import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { FileCheck, Plus, Edit, Trash2, Check, X, GripVertical, Lock, Unlock } from 'lucide-react';
import { getFamilyDocumentTypes, createFamilyDocumentType, updateFamilyDocumentType, deleteFamilyDocumentType } from '../../api';
import { useToast } from '../../contexts/NotificationContext';

const FIELD_TYPES = [
  { value: 'checkbox', label: 'Case à cocher' },
  { value: 'date_validity', label: 'Date avec validité' },
];

const FamilyDocumentTypeManagement = () => {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ name: '', position: 0, fieldType: 'checkbox', validityMonths: 12, persistent: true });
  const [editId, setEditId] = useState(null);
  const [editData, setEditData] = useState({ name: '', position: 0, fieldType: 'checkbox', validityMonths: 12, persistent: true });
  const toast = useToast();

  const fetchTypes = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getFamilyDocumentTypes();
      setTypes(res.data);
    } catch (err) {
      console.error('[FamilyDocTypes]', err);
      toast.error('Erreur chargement types documents');
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchTypes(); }, [fetchTypes]);

  const handleCreate = async () => {
    if (!formData.name.trim()) return toast.error('Nom obligatoire');
    try {
      await createFamilyDocumentType(formData);
      toast.success('Type de document créé');
      setShowForm(false);
      setFormData({ name: '', position: types.length, fieldType: 'checkbox', validityMonths: 12, persistent: true });
      fetchTypes();
    } catch (err) {
      toast.error('Erreur création');
    }
  };

  const startEdit = (t) => {
    setEditId(t.id);
    setEditData({ name: t.name, position: t.position, fieldType: t.fieldType, validityMonths: t.validityMonths || 12, persistent: t.persistent !== false });
  };

  const handleUpdate = async () => {
    if (!editData.name.trim()) return toast.error('Nom obligatoire');
    try {
      await updateFamilyDocumentType(editId, editData);
      toast.success('Type mis à jour');
      setEditId(null);
      fetchTypes();
    } catch (err) {
      toast.error('Erreur mise à jour');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce type de document ? Les checks associés seront supprimés.')) return;
    try {
      await deleteFamilyDocumentType(id);
      toast.success('Type supprimé');
      fetchTypes();
    } catch (err) {
      toast.error('Erreur suppression');
    }
  };

  return (
<div className="px-4 sm:px-6 lg:px-8 space-y-6"><Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2">
          <FileCheck size={18} className="text-amber-500" />
          Types documents familles
        </CardTitle>
        <button
          onClick={() => { setShowForm(!showForm); setFormData({ name: '', position: types.length, fieldType: 'checkbox', validityMonths: 12, persistent: true }); }}
          className="flex items-center gap-1 px-3 py-1.5 bg-primary text-white rounded-lg text-sm hover:bg-primary-dark transition"
        >
          <Plus size={14} />
          Ajouter
        </button>
      </CardHeader>
      <CardContent>
        {showForm && (
          <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <div>
                <label className="text-xs font-medium text-gray-600 block mb-1">Nom *</label>
                <input
                  type="text"
                  placeholder="Ex: Certificat médical"
                  value={formData.name}
                  onChange={e => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 block mb-1">Type de champ</label>
                <select
                  value={formData.fieldType}
                  onChange={e => setFormData({ ...formData, fieldType: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  {FIELD_TYPES.map(ft => <option key={ft.value} value={ft.value}>{ft.label}</option>)}
                </select>
              </div>
              {formData.fieldType === 'date_validity' && (
                <div>
                  <label className="text-xs font-medium text-gray-600 block mb-1">Validité (mois)</label>
                  <input
                    type="number"
                    min="1"
                    value={formData.validityMonths}
                    onChange={e => setFormData({ ...formData, validityMonths: parseInt(e.target.value) || 12 })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
              )}
              <div>
                <label className="text-xs font-medium text-gray-600 block mb-1">Position (ordre)</label>
                <input
                  type="number"
                  min="0"
                  value={formData.position}
                  onChange={e => setFormData({ ...formData, position: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.persistent}
                onChange={e => setFormData({ ...formData, persistent: e.target.checked })}
                className="rounded border-gray-300 text-primary focus:ring-primary"
              />
              <span className="text-sm text-gray-700">Persistant entre années scolaires</span>
              <span className="text-xs text-gray-400">(ex: carte d'identité — pas besoin de redemander)</span>
            </label>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setShowForm(false)} className="px-3 py-1.5 text-sm text-gray-500 hover:text-gray-700">Annuler</button>
              <button onClick={handleCreate} className="px-4 py-1.5 bg-primary text-white rounded-lg text-sm hover:bg-primary-dark transition">Créer</button>
            </div>
          </div>
        )}

        {loading ? (
          <p className="text-gray-400 text-sm">Chargement...</p>
        ) : types.length === 0 ? (
          <p className="text-gray-400 text-sm text-center py-6">Aucun type de document configuré.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left px-3 py-2 font-semibold text-gray-600">Pos.</th>
                  <th className="text-left px-3 py-2 font-semibold text-gray-600">Nom</th>
                  <th className="text-center px-3 py-2 font-semibold text-gray-600">Type</th>
                  <th className="text-center px-3 py-2 font-semibold text-gray-600">Validité</th>
                  <th className="text-center px-3 py-2 font-semibold text-gray-600">Persistant</th>
                  <th className="text-right px-3 py-2 font-semibold text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {types.map(t => (
                  <tr key={t.id} className="border-b border-gray-100 hover:bg-gray-50 transition">
                    {editId === t.id ? (
                      <>
                        <td className="px-3 py-2">
                          <input type="number" min="0" value={editData.position}
                            onChange={e => setEditData({ ...editData, position: parseInt(e.target.value) || 0 })}
                            className="w-14 px-2 py-1 border border-gray-300 rounded text-sm" />
                        </td>
                        <td className="px-3 py-2">
                          <input type="text" value={editData.name}
                            onChange={e => setEditData({ ...editData, name: e.target.value })}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm" />
                        </td>
                        <td className="px-3 py-2 text-center">
                          <select value={editData.fieldType}
                            onChange={e => setEditData({ ...editData, fieldType: e.target.value })}
                            className="px-2 py-1 border border-gray-300 rounded text-sm">
                            {FIELD_TYPES.map(ft => <option key={ft.value} value={ft.value}>{ft.label}</option>)}
                          </select>
                        </td>
                        <td className="px-3 py-2 text-center">
                          {editData.fieldType === 'date_validity' ? (
                            <input type="number" min="1" value={editData.validityMonths}
                              onChange={e => setEditData({ ...editData, validityMonths: parseInt(e.target.value) || 12 })}
                              className="w-16 px-2 py-1 border border-gray-300 rounded text-sm" />
                          ) : <span className="text-gray-400">—</span>}
                        </td>
                        <td className="px-3 py-2 text-center">
                          <label className="flex items-center justify-center gap-1 cursor-pointer">
                            <input type="checkbox" checked={editData.persistent}
                              onChange={e => setEditData({ ...editData, persistent: e.target.checked })}
                              className="rounded border-gray-300 text-primary focus:ring-primary" />
                          </label>
                        </td>
                        <td className="px-3 py-2 text-right">
                          <div className="flex gap-1 justify-end">
                            <button onClick={handleUpdate} className="p-1.5 text-green-600 hover:bg-green-50 rounded"><Check size={14} /></button>
                            <button onClick={() => setEditId(null)} className="p-1.5 text-gray-400 hover:bg-gray-100 rounded"><X size={14} /></button>
                          </div>
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="px-3 py-2 text-gray-400">
                          <span className="flex items-center gap-1"><GripVertical size={12} className="text-gray-300" />{t.position}</span>
                        </td>
                        <td className="px-3 py-2 font-medium text-gray-800">{t.name}</td>
                        <td className="px-3 py-2 text-center">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                            t.fieldType === 'date_validity' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'
                          }`}>
                            {FIELD_TYPES.find(ft => ft.value === t.fieldType)?.label || t.fieldType}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-center text-gray-500">
                          {t.fieldType === 'date_validity' ? `${t.validityMonths || 12} mois` : '—'}
                        </td>
                        <td className="px-3 py-2 text-center">
                          {t.persistent !== false ? (
                            <span className="inline-flex items-center gap-1 text-xs text-green-600" title="Conservé entre années scolaires">
                              <Lock size={12} /> Oui
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-xs text-amber-600" title="À renouveler chaque année scolaire">
                              <Unlock size={12} /> Annuel
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <div className="flex gap-1 justify-end">
                            <button onClick={() => startEdit(t)} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"><Edit size={14} /></button>
                            <button onClick={() => handleDelete(t.id)} className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded"><Trash2 size={14} /></button>
                          </div>
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <p className="text-xs text-gray-400 mt-4">
          Les types de documents sont partagés entre toutes les structures. La position détermine l'ordre d'affichage.
          <br />
          <strong>Persistant</strong> = le document reste coché d'une année sur l'autre (ex: carte d'identité).
          <strong> Annuel</strong> = à renouveler chaque année scolaire (ex: certificat médical).
        </p>
      </CardContent>
    </Card></div>
  );
};

export default FamilyDocumentTypeManagement;
